import * as React from 'react';

import { Button, useMemoDataGridRows } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle } from 'lucide-react';

import * as formState from '../../_state/billForm.state';
import * as state from '../../_state/financeBills.state';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { BillRecord } from '~/modules/finance/types';

//
// ⚛️ ⚛️ ⚛️ BILLS LIST ACTIONS ⚛️ ⚛️ ⚛️
//
export const BillsListActions = React.memo(() => {
  // Translations
  const t = useTranslations();

  // Atoms
  const { data: { bills } = { bills: [] } } = useAtomValue(state.$remoteBills);
  const selectedKeys = useAtomValue(state.$selectedKeys);
  const setBillsToPay = useSetAtom(state.$billsToPay);
  const setIsPaymentDrawerOpen = useSetAtom(state.$isPaymentDrawerOpen);
  const setIsWizardOpen = useSetAtom(state.$isBillWizardOpen);
  const resetBillForm = useSetAtom(formState.$resetBillForm);

  // Data hooks
  const toast = useToast();
  const selectedRows = useMemoDataGridRows(bills, selectedKeys);

  // Callbacks
  const handlePay = React.useCallback(
    (billsToPaySelected: BillRecord[]) => {
      const allPending = billsToPaySelected.every(
        (bill) => bill.status === 'Pending',
      );
      if (!allPending) {
        toast.error(t.bills.list.tooltips.notAllPending);
        return;
      }
      setBillsToPay(billsToPaySelected);
      setIsPaymentDrawerOpen(true);
    },
    [toast, t, setBillsToPay, setIsPaymentDrawerOpen],
  );

  const handleCreateClick = React.useCallback(() => {
    resetBillForm();
    setIsWizardOpen(true);
  }, [resetBillForm, setIsWizardOpen]);

  return (
    <>
      {selectedRows.length > 0 && (
        <Button
          color="primary"
          variant="outline"
          onPress={() => handlePay(selectedRows)}
        >
          {t.bills.list.buttons.payMultiple({
            count: selectedRows.length,
            type: selectedRows.length === 1 ? 'Bill' : 'Bills',
          })}
        </Button>
      )}
      <Button
        color="primary"
        variant="solid"
        icon={<LucidePlusCircle className="size-4" />}
        onPress={handleCreateClick}
      >
        {t.bills.list.buttons.create}
      </Button>
    </>
  );
});
